import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, CreditCard, Eye } from 'lucide-react';
import { API, BACKEND_URL, useAuthHeader } from './hooks';

const statusBadge = (status) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    delivered: 'bg-blue-100 text-blue-800',
    redeemed: 'bg-purple-100 text-purple-800',
    expired: 'bg-red-100 text-red-800',
  };
  return styles[status] || 'bg-gray-100 text-gray-800';
};

const GiftCardsManager = () => {
  const getAuthHeader = useAuthHeader();
  const [cards, setCards] = useState([]);
  const [paymentRef, setPaymentRef] = useState('');
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemBy, setRedeemBy] = useState('');

  const fetchCards = async () => {
    try {
      const res = await axios.get(`${API}/gift-cards`, getAuthHeader());
      setCards(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchCards(); }, []);

  const confirmPayment = async (id) => {
    if (!paymentRef.trim()) {
      toast.error('Please enter payment reference');
      return;
    }
    try {
      const response = await axios.patch(
        `${API}/gift-cards/${id}/confirm-payment`,
        { payment_reference: paymentRef },
        getAuthHeader()
      );
      toast.success('Payment confirmed!');
      setPaymentRef('');
      setSelectedCardId(null);
      fetchCards();
      if (response.data.whatsapp_delivery_url) {
        window.open(response.data.whatsapp_delivery_url, '_blank');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm payment');
    }
  };

  const redeem = async () => {
    if (!redeemCode.trim()) {
      toast.error('Please enter gift card code');
      return;
    }
    try {
      await axios.post(
        `${API}/gift-cards/redeem`,
        { code: redeemCode, redeemed_by: redeemBy || 'Admin' },
        getAuthHeader()
      );
      toast.success('Gift card redeemed successfully!');
      setRedeemCode('');
      setRedeemBy('');
      fetchCards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to redeem');
    }
  };

  return (
    <div data-testid="giftcards-content">
      <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
        <h2 className="font-heading text-3xl flex items-center gap-3">
          <Gift size={28} /> Gift Cards
        </h2>

        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Code to redeem"
            value={redeemCode}
            onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
            data-testid="redeem-code-input"
            className="border-2 border-gray-300 px-3 py-2 font-mono text-sm w-40"
          />
          <input
            type="text"
            placeholder="Client name"
            value={redeemBy}
            onChange={(e) => setRedeemBy(e.target.value)}
            data-testid="redeem-by-input"
            className="border-2 border-gray-300 px-3 py-2 font-mono text-sm w-32"
          />
          <button
            onClick={redeem}
            data-testid="redeem-btn"
            className="bg-purple-600 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-purple-700"
          >
            Redeem
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Pending" value={cards.filter(g => g.payment_status === 'unpaid').length} color="yellow" />
        <Stat label="Paid" value={cards.filter(g => g.payment_status === 'paid' && g.status !== 'redeemed').length} color="green" />
        <Stat label="Redeemed" value={cards.filter(g => g.status === 'redeemed').length} color="purple" />
        <Stat label="Total Value" value={`₹${cards.filter(g => g.payment_status === 'paid').reduce((s, g) => s + g.amount, 0).toLocaleString()}`} color="gray" />
      </div>

      <div className="space-y-4">
        {cards.map((card) => (
          <div key={card.id} className="bg-white border border-gray-200 p-6" data-testid={`giftcard-item-${card.id}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-2xl font-bold">₹{card.amount.toLocaleString()}</h3>
                  <span className={`font-mono text-xs uppercase px-3 py-1 rounded-full ${statusBadge(card.status)}`}>
                    {card.status}
                  </span>
                </div>
                <p className="font-mono text-sm text-gray-500">Code: <span className="font-bold text-black">{card.code}</span></p>
              </div>
              <p className="font-mono text-xs text-gray-400">{new Date(card.created_at).toLocaleDateString()}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
              <Field label="From" value={card.sender_name} />
              <Field label="To" value={card.recipient_name} />
              <Field label="Expiry" value={new Date(card.expiry_date).toLocaleDateString()} />
              <Field label="Phone" value={card.sender_phone} />
            </div>

            {card.payment_status === 'unpaid' && (
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                {selectedCardId === card.id ? (
                  <>
                    <input
                      type="text"
                      placeholder="UPI Ref / Screenshot ID"
                      value={paymentRef}
                      onChange={(e) => setPaymentRef(e.target.value)}
                      data-testid={`payment-ref-${card.id}`}
                      className="border-2 border-gray-300 px-3 py-2 font-mono text-sm w-48"
                    />
                    <button
                      onClick={() => confirmPayment(card.id)}
                      data-testid={`confirm-payment-${card.id}`}
                      className="bg-green-600 text-white px-4 py-2 font-mono text-xs uppercase hover:bg-green-700"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => { setSelectedCardId(null); setPaymentRef(''); }}
                      className="bg-gray-300 text-gray-700 px-3 py-2 font-mono text-xs uppercase hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setSelectedCardId(card.id)}
                    data-testid={`mark-paid-${card.id}`}
                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 font-mono text-xs uppercase hover:bg-green-700"
                  >
                    <CreditCard size={14} /> Mark as Paid
                  </button>
                )}
              </div>
            )}

            {card.image_url && (
              <a
                href={`${BACKEND_URL}${card.image_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 font-mono text-xs uppercase hover:bg-blue-700 mt-4"
              >
                <Eye size={14} /> View Gift Card
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Stat = ({ label, value, color }) => {
  const colorMap = {
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  return (
    <div className={`border p-4 text-center ${colorMap[color]}`}>
      <p className="font-mono text-xs uppercase opacity-80">{label}</p>
      <p className="font-heading text-2xl font-bold">{value}</p>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="font-mono text-xs uppercase text-gray-500">{label}</p>
    <p className="font-bold">{value}</p>
  </div>
);

export default GiftCardsManager;
